/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { useFormik } from 'formik';
import { Form } from 'react-bootstrap';
import * as Yup from 'yup';
import clsx from 'clsx';

const claimSchema = Yup.object().shape({
  memberId: Yup.string().required('Member ID is required'),
  mobileNo: Yup.string()
    .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
    .required('Mobile number is required'),
  emailId: Yup.string().email('Invalid email').required('Email is required'),
  claimDateOfAdmission: Yup.date().required('Date of admission is required'),
  claimDateOfDischarge: Yup.date()
    .min(Yup.ref('claimDateOfAdmission'), 'Discharge date must be after admission date')
    .required('Date of discharge is required'),
  hospName: Yup.string().required('Hospital name is required'),
  hospAddress: Yup.string().required('Hospital address is required'),
  reasonForHospitalization: Yup.string().required('Reason for hospitalization is required'),
  disease: Yup.string().required('Disease is required'),
  claimAmount: Yup.number()
    .min(1, 'Claim amount must be greater than 0')
    .required('Claim amount is required'),
});

type Props = {
  memberList: { id: string; name: string; relation: string }[];
  policyId: number;
  employeeId: string | undefined;
  email: string | undefined;
  onSubmitSuccess?: () => void;
};

export function ClaimIntimationForm({ memberList, policyId, employeeId, email, onSubmitSuccess }: Props) {
  const initialValues = {
    memberId: '',
    mobileNo: '',
    emailId: email || '',
    claimDateOfAdmission: '',
    claimDateOfDischarge: '',
    hospName: '',
    hospAddress: '',
    reasonForHospitalization: '',
    disease: '',
    claimAmount: '',
    claimType: 'HOSPITALIZATION', // Fixed value
    policyId, // Include policyId
    employeeId, // Include employeeId
  };

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const formik = useFormik({
    initialValues,
    validationSchema: claimSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setSuccessMessage(null);
      try {
        const response = await fetch('https://etl.futurisk.in/webhook/ClaimIntimation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          throw new Error('Failed to submit claim');
        }

        setSuccessMessage('Claim submitted successfully');
        setLoading(false);
        formik.resetForm();

        if (onSubmitSuccess) {
          setTimeout(() => {
            onSubmitSuccess();
          }, 1000);
        }
      } catch (error) {
        console.error(error);
        setStatus('Submission failed. Please try again.');
        setLoading(false);
      }
    },
  });

  return (
    <>
      {successMessage && (
        <div className='mb-lg-15 alert alert-success'>
          <div className='alert-text font-weight-bold'>{successMessage}</div>
          <button
            className='btn btn-sm btn-primary mt-3'
            onClick={() => window.location.reload()}
          >
            Continue
          </button>
        </div>
      )}
      {!successMessage && (
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <div className='row'>
            {/* Member ID */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Member</label>
              <Form.Select
                size='lg'
                className='form-select form-select-solid form-select-lg fw-bold'
                {...formik.getFieldProps('memberId')}
              >
                <option value=''>Select Member</option>
                {memberList.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.relation})
                  </option>
                ))}
              </Form.Select>
              {formik.touched.memberId && formik.errors.memberId && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.memberId}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Number */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Mobile Number</label>
              <input
                placeholder='Mobile Number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('mobileNo')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.mobileNo && formik.errors.mobileNo,
                  },
                  {
                    'is-valid': formik.touched.mobileNo && !formik.errors.mobileNo,
                  }
                )}
              />
              {formik.touched.mobileNo && formik.errors.mobileNo && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.mobileNo}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Email */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Email</label>
              <input
                placeholder='Email'
                type='email'
                autoComplete='off'
                disabled
                {...formik.getFieldProps('emailId')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.emailId && formik.errors.emailId,
                  },
                  {
                    'is-valid': formik.touched.emailId && !formik.errors.emailId,
                  }
                )}
              />
              {formik.touched.emailId && formik.errors.emailId && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.emailId}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Claim Dates */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Date of Admission</label>
              <input
                type='date'
                {...formik.getFieldProps('claimDateOfAdmission')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.claimDateOfAdmission && formik.errors.claimDateOfAdmission,
                  },
                  {
                    'is-valid': formik.touched.claimDateOfAdmission && !formik.errors.claimDateOfAdmission,
                  }
                )}
              />
              {formik.touched.claimDateOfAdmission && formik.errors.claimDateOfAdmission && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.claimDateOfAdmission}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Date of Discharge</label>
              <input
                type='date'
                {...formik.getFieldProps('claimDateOfDischarge')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.claimDateOfDischarge && formik.errors.claimDateOfDischarge,
                  },
                  {
                    'is-valid': formik.touched.claimDateOfDischarge && !formik.errors.claimDateOfDischarge,
                  }
                )}
              />
              {formik.touched.claimDateOfDischarge && formik.errors.claimDateOfDischarge && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.claimDateOfDischarge}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Hospital Details */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Hospital Name</label>
              <input
                placeholder='Hospital Name'
                type='text'
                {...formik.getFieldProps('hospName')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.hospName && formik.errors.hospName,
                  },
                  {
                    'is-valid': formik.touched.hospName && !formik.errors.hospName,
                  }
                )}
              />
              {formik.touched.hospName && formik.errors.hospName && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.hospName}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Hospital Address</label>
              <input
                placeholder='Hospital Address'
                type='text'
                {...formik.getFieldProps('hospAddress')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.hospAddress && formik.errors.hospAddress,
                  },
                  {
                    'is-valid': formik.touched.hospAddress && !formik.errors.hospAddress,
                  }
                )}
              />
              {formik.touched.hospAddress && formik.errors.hospAddress && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.hospAddress}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Reason and Disease */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Reason for Hospitalization</label>
              <input
                placeholder='Reason for Hospitalization'
                type='text'
                {...formik.getFieldProps('reasonForHospitalization')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.reasonForHospitalization && formik.errors.reasonForHospitalization,
                  },
                  {
                    'is-valid': formik.touched.reasonForHospitalization && !formik.errors.reasonForHospitalization,
                  }
                )}
              />
              {formik.touched.reasonForHospitalization && formik.errors.reasonForHospitalization && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.reasonForHospitalization}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Disease</label>
              <input
                placeholder='Disease'
                type='text'
                {...formik.getFieldProps('disease')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.disease && formik.errors.disease,
                  },
                  {
                    'is-valid': formik.touched.disease && !formik.errors.disease,
                  }
                )}
              />
              {formik.touched.disease && formik.errors.disease && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.disease}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Claim Amount */}
            <div className='col-md-6 fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Claim Amount</label>
              <input
                placeholder='Claim Amount'
                type='number'
                {...formik.getFieldProps('claimAmount')}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.claimAmount && formik.errors.claimAmount,
                  },
                  {
                    'is-valid': formik.touched.claimAmount && !formik.errors.claimAmount,
                  }
                )}
              />
              {formik.touched.claimAmount && formik.errors.claimAmount && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.claimAmount}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className='text-end'> {/* Align button to the right */}
            <button
              type='submit'
              className='btn btn-sm btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Submit</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
